import React, { FC, useEffect, useRef, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import {FaPlus, FaBan, FaAngleDoubleRight, FaAngleDoubleLeft, FaRetweet, FaCheckCircle, FaEdit, FaExclamationCircle} from 'react-icons/fa';
import '../../styles/components.scss';
import './PlanSelector.scss';
import { ContentCard } from '../Cards';
import {
    fetchAddPolicy,
    fetchModifyPolicy,
    fetchRemovePolicy,
    fetchUpdatePackageProgress,
    fetchUndoRemovePolicy
} from '../../actions/EnrollmentActions';
import { getPlanRates } from '../../apiCalls/rates/rateApiCalls';
import { rateTypeDependent, rateTypeVolume, rateTypeFSASelectMatch } from './RateType';
import { formatCurrency } from '../../utils/CurrencyHelper';
import { stepNumberHelper } from '../../utils/StepNumberHelper';
import DeleteConfirm from '../DeleteConfirm/DeleteConfirm';
import { fetchCartInfo } from '../../actions/CartActions';
import { PlanDocuments } from '../PlanDocuments';
import ChangeNotification from './ChangeNotification';
import { debounce } from "../../utils/Debounce";
import moment from 'moment';
import { AvailablePlanType, Package, PackageStepType } from '../../types/api/enrollmentPackages/Package';
import { EnrollmentPackageId, PersonId, PlanAvailableToClassId, PlanTypeId, PolicyId } from '../../types/api/primaryKeys/PrimaryKey';
import { useMounted } from '../../hooks/components/useMounted';
import { useWolfTracsState } from '../../hooks/redux/useWolfTracsState';
import { useNumberIdsFromRoute } from '../../hooks/routing/useNumberIdsFromRoute';
import { Nullable } from '../../types/custom/Nullable';
import { PlanRateRequest } from '../../types/api/rates/PlanRateRequest';
import { PlanRateResponse } from '../../types/api/rates/PlanRateResponse';
import { AvailablePlanDetail, AvailablePlansDetail, PersonRelative } from '../../types/api/plans/AvailablePlansDetail';
import { ApiDate } from '../../types/custom/ApiDate';
import { useReduxDispatch } from '../../hooks/redux/useReduxDispatch';
import { CarrierImage } from '../Images/CarrierImage';
import { VolumeSource } from '../../types/enums/VolumeSource';
import { Decimal } from '../../types/custom/Decimal';
import { getAvailablePlansForPlanType } from '../../apiCalls/policies/policyApiCalls';
import { Button, ButtonGroup, ButtonToolbar, Card, Form, ListGroup, Table } from 'react-bootstrap';
import classnames from 'classnames';

interface Props {
    packageId: EnrollmentPackageId;
}

type VolumeMapper = Nullable<Decimal>[];

const PlanSelector: FC<Props> = ({ packageId }) => {

    const isMounted = useMounted();

    // Routing
    const history = useHistory();
    const match = useRouteMatch();

    // Redux
    const dispatch = useReduxDispatch();

    // Route Params
    const { planTypeId } = useNumberIdsFromRoute() as { planTypeId: PlanTypeId };

    // Local State
    const [ availablePlanData, setAvailablePlanData ] = useState<Nullable<AvailablePlansDetail>>(null);
    const [ ratesList, setRatesList ] = useState<readonly PlanRateResponse[]>([]);

    const [ volumeMapper, setVolumeMapper ] = useState<VolumeMapper>([]);
    const [ inFlightStringVolumeMapper, setInFlightStringVolumeMapper ] = useState<Nullable<string>[]>([]);

    const [ availablePlansLoading, setAvailablePlansLoading ] = useState(false);
    const [ planRatesLoading, setPlanRatesLoading ] = useState(false);
    const [ saveAndContinueLoading, setSaveAndContinueLoading ] = useState(false);
    
    // Local Refs

    // Note - in the original implementation, this was in state. I don't see anywhere it's affecting render,
    // so i'm going to try this implementation in a ref instead.
    const participantsList = useRef<PersonId[]>([]);

    // This is also nowhere being used for actual render logic. Moving it into a ref.
    const useDefinedContribution = useRef(false);

    // This also used to be in state, but it doesn't affect render. It probably doesn't need to exist, at all.
    const callsCompleted = useRef(0);

    // Redux State
    const { data: activePackage } = useWolfTracsState(s => s.packages.activePackage);
    const shouldHideEmployeeContributions = activePackage === null || activePackage.hideEmployeeContributionInEnrollment;
    const shouldHideEmployerContributions = activePackage === null || activePackage.hideEmployerContributionInEnrollment;
    const shouldHideAllContributionInformation = shouldHideEmployeeContributions && shouldHideEmployerContributions;

    // DOM Refs
    const volumeInput = useRef<HTMLInputElement>(null);

    // ToDo: Warning: This is NOT ideal, but working out the true dependency logic will take more time.
    // fetchOrRefresh was not originally written in any other way other than "run when the plan type changes" and isn't built to account for
    // other variable updates.
    useEffect(() => {
        fetchOrRefresh();
    }, [packageId, planTypeId]);

    // Main Data Retrieval
    const fetchOrRefresh = async (goToBeneficiaries = false, getRates = false) => {
        // Dependent on - ActivePackage.planTypes, packageid, planTypeId

        if (!isMounted) {
            return;
        }

        // Begin logic.

        // Reset all component values - this method being called indicates some sort of reset.
        callsCompleted.current = 0;
        setRatesList([]);
        useDefinedContribution.current = false;
        participantsList.current = [];
        setInFlightStringVolumeMapper([]);

        // Set a main loading state.
        setAvailablePlansLoading(true);

        // Sanity check - in case this component's life cycle is longer than expected, 
        setSaveAndContinueLoading(false);

        const availablePlansApiResponse = await getAvailablePlansForPlanType(packageId, planTypeId);

        if (!isMounted) {
            return;
        }

        // Place this data in state, because we need it for rendering and future reference.
        // However, the data is also needed immediately below as well.
        setAvailablePlanData(availablePlansApiResponse);

        // Note: below this point maybe should be a separate effect reacting to the plan selection data changing?
        // Though I'm not sure how this "goToBeneficiaries" trigger would work in that case.

        // This serves as a shortcut - redirect that can occur if the caller specifies it to happen.
        if (goToBeneficiaries) {
            const policyId = availablePlansApiResponse.selectedPolicyId;
            history.push(`${match.url}/policy-subscription/${policyId}/beneficiaries`);
            return;
        }
        
        const mappedVolumes: VolumeMapper = [];

        availablePlansApiResponse.availablePlans.forEach((ap, planIndex) => {
            if (ap.existingPolicyParticipants) {
                ap.existingPolicyParticipants.forEach(epp => {
                    participantsList.current.push(epp);
                });
            }

            // I'm not sure honestly if null is / should be possible. According to some of the code below, it is.
            let startingVolumeValue: Nullable<Decimal>;

            // Lock in the volume to the max if that is provided as a setting.
            if (ap.volumeIsLockedToMaximum) {
                startingVolumeValue = ap.maximumAvailableVolume;
            }
            // There's an existing policy - use the volume set there if it's available.
            else if (ap.existingPolicyId && ap.existingPolicyId > 0) {
                startingVolumeValue = ap.existingPolicyVolumeInUse;
            }
            // SPECIAL CASE - Troy wants that, for FSA specifically (unfortunately HRA has different rules), instead of defaulting volume to the minimum, we default to zero.
            // IMPORTANT! -- This logic is DUPLICATED on the back end! Basically all of this, not just this condition! GetPlansAvailableToPackageByPlanType, line 1411ish.
            // There is not a way I feel comfortable right now preventing that. This is dangerous and error prone, but I don't know what else to do without a complete rewrite.
            else if (ap.rateTypeId === rateTypeFSASelectMatch() && ap.planTypeShortName !== 'HRA') {
                startingVolumeValue = 0;
            }
            // Default case - default volume to minimum.
            else {
                startingVolumeValue = ap.minimumAvailableVolume;
            }

            mappedVolumes[planIndex] = startingVolumeValue;
        });

        if (availablePlansApiResponse.dependentsAreFrozen) {
            participantsList.current = availablePlansApiResponse.frozenParticipants!;
        }

        // force dc dollars on, if they can be used
        const dcUsed = availablePlansApiResponse.canUseDefinedContribution;

        if(isMounted) {
            useDefinedContribution.current = dcUsed;

            // This will always be setting a new volume mapper instance.
            setVolumeMapper(mappedVolumes);

            const cartNeedsFetch = availablePlansApiResponse.availablePlans.some(ap => ap.hasExistingPolicy);

            if (cartNeedsFetch) {
                dispatch(fetchCartInfo(packageId));
            }
            if (getRates) {
                fetchPlanRates();
            }

            setAvailablePlansLoading(false);         
        }
    };

    const isSelected = (policyId: PolicyId, personId: PersonId) => {
        // This "should" never happen. Just handling it.
        if (!availablePlanData) {
            return false;
        }

        let selected = false;

        if (availablePlanData.dependentsAreFrozen && availablePlanData.frozenParticipants) {
            if (availablePlanData.frozenParticipants.indexOf(personId) !== -1) {
                selected = true;
            }
        }
        else {
            // Todo: Replace with "find".
            availablePlanData.availablePlans.forEach(ap => {
                if (ap.existingPolicyId === policyId
                    && ap.existingPolicyParticipants
                    && ap.existingPolicyParticipants.indexOf(personId) !== -1
                    && !ap.toBeRemoved) {
                    selected = true;
                }
            });
        }
        return selected;
    };

    const onAddPolicy = async (classPlanId: PlanAvailableToClassId, startDate: ApiDate, planIndex: number, plan: Readonly<AvailablePlanDetail>) => {
        if(!isMounted || !activePackage) {
            return;
        }

        setSaveAndContinueLoading(true);

        let madeChanges = false;
        if (activePackage.lastCompletedStepNumber === stepNumberHelper.stepNumberForNamedStep(activePackage, PackageStepType.Finish)) {
            ChangeNotification();
            madeChanges = true;
        }

        const selectedVolume = volumeMapper[planIndex] ?? 0;

        await dispatch(fetchAddPolicy(packageId, classPlanId, startDate, participantsList.current, selectedVolume, useDefinedContribution.current));   
        await dispatch(fetchUpdatePackageProgress(packageId, null));

        if (plan.isBeneficiariesRequired || madeChanges) {
            fetchOrRefresh(true, false);
        } else {
            onPageForward();
        }
    };

    const viewBeneficiaries = () => {
        const policyId = availablePlanData?.selectedPolicyId;
        if (policyId) {
            history.push(`${match.url}/policy-subscription/${policyId}/beneficiaries`);
        }
    };

    const onChangeTo = async (classPlanId: PlanAvailableToClassId, startDate: ApiDate, planIndex: number, plan: Readonly<AvailablePlanDetail>) => {
        if (!isMounted || !availablePlanData) {
            return;
        }

        let policyId: Nullable<PolicyId> = null;

        // ToDo: Does this never work for brand new policies..?
        availablePlanData.availablePlans.forEach(ap => {
            if (ap.existingPolicyId && !ap.toBeRemoved) {
                policyId = ap.existingPolicyId;
            }
        });

        if (policyId !== null) {
            setSaveAndContinueLoading(true);
            await dispatch(fetchRemovePolicy(packageId, policyId));

            if(isMounted) {
                setSaveAndContinueLoading(false);
                onAddPolicy(classPlanId, startDate, planIndex, plan);
            }
        }
    };

    const onChangeBackTo = async (policyIdToChangeBackTo: PolicyId) => {
        if (!isMounted || !activePackage || !availablePlanData) {
            return;
        }

        let policyId: Nullable<PolicyId> = null;
        availablePlanData.availablePlans.forEach(ap => {
            if (ap.existingPolicyId && !ap.toBeRemoved) {
                policyId = ap.existingPolicyId;
            }
        });

        if (policyId !== null) {
            setSaveAndContinueLoading(true);

            let madeChanges = false;
            if (activePackage.lastCompletedStepNumber === stepNumberHelper.stepNumberForNamedStep(activePackage, PackageStepType.Finish)) {
                ChangeNotification();
                madeChanges = true;
            }
            await dispatch(fetchRemovePolicy(packageId, policyId));

            if (isMounted) {
                setSaveAndContinueLoading(false);
                await onUndoRemovePolicy(policyIdToChangeBackTo, madeChanges);
            }
        }
    };

    const onRemovePolicy = async (policyId: PolicyId) => {
        if(!isMounted || !activePackage) {
            return;
        }

        setSaveAndContinueLoading(true);

        if (activePackage.lastCompletedStepNumber === stepNumberHelper.stepNumberForNamedStep(activePackage, PackageStepType.Finish)) {
            ChangeNotification();
        }
        await dispatch(fetchRemovePolicy(packageId, policyId));
        await dispatch(fetchUpdatePackageProgress(packageId, null, true));

        if(isMounted) {
            setSaveAndContinueLoading(false);
            fetchOrRefresh(false, false);
        }
    };

    const onUndoRemovePolicy = async (policyId: PolicyId, notificationHasHappened = false) => {
        if (!isMounted || !activePackage) {
            return;
        }

        setSaveAndContinueLoading(true);
  
        if (!notificationHasHappened && activePackage.lastCompletedStepNumber === stepNumberHelper.stepNumberForNamedStep(activePackage, PackageStepType.Finish)) {
            ChangeNotification();   
        }


        await dispatch(fetchUndoRemovePolicy(packageId, policyId));
        await dispatch(fetchUpdatePackageProgress(packageId, null, true));

        if (isMounted) {
            setSaveAndContinueLoading(false);
            fetchOrRefresh(false, true);
        }
    };

    const onDecline = () => {
        if (!isMounted || !activePackage) {
            return;
        }

        if (activePackage.lastCompletedStepNumber === stepNumberHelper.stepNumberForNamedStep(activePackage, PackageStepType.Finish)) {
            ChangeNotification();
        }

        fetchOrRefresh(false, false);
        onPageForward();
    };

    const onPageBack = () => {
        if (!activePackage) {
            return;
        }
        history.push(getPreviousStepRoute(activePackage, planTypeId));
    };

    const getEnrollmentContentFromCurrentStep = () => {
        if (activePackage) {
            const currentStepNumber = stepNumberHelper.stepNumberForPlanTypeStep(activePackage, planTypeId);
            if (currentStepNumber !== null) {
                return activePackage.steps[currentStepNumber - 1].enrollmentContent;
            }
        }
        
        return '';
    };

    const onPageForward = async () => {
        if (!activePackage) {
            return;
        }

        const currentStepNumber = stepNumberHelper.stepNumberForPlanTypeStep(activePackage, planTypeId);

        const updatePackageResults = await dispatch(fetchUpdatePackageProgress(packageId, currentStepNumber, true));

        history.push(getNextStepRoute(updatePackageResults, planTypeId));
    };

    const onSaveAndContinue = (policyId: PolicyId) => {
        if(!isMounted || !activePackage || !availablePlanData) {
            return;
        }

        setSaveAndContinueLoading(true);
       
        if (activePackage.lastCompletedStepNumber === stepNumberHelper.stepNumberForNamedStep(activePackage, PackageStepType.Finish)) {
            ChangeNotification();
        }

        availablePlanData.availablePlans.forEach((ap, planIndex) => {
            if (ap.hasExistingPolicy) {

                // IMPORTANT! The parenthesis around the second half of this statement are CRITICAL - removing them will result in an
                // order of operations error that I myself was not aware of. Removing them results in the null coalesce running first, and THEN that result
                // running against the ternary. The ternary should ONLY run if volumeMapper at plan index is not available!
                const volumeToUse = volumeMapper[planIndex] ?? (ap.existingPolicyVolumeInUse ? ap.existingPolicyVolumeInUse : 0);

                dispatch(fetchModifyPolicy(packageId, policyId, ap.rates.effectiveDate, participantsList.current, volumeToUse)).then(() => {
                    if (isMounted) {
                        callsCompleted.current++;
                        if (availablePlanData.availablePlans.length >= callsCompleted.current) {
                            if (availablePlanData.availablePlans.some(plan => plan.hasExistingPolicy && plan.isBeneficiariesRequired)) {
                                const policyId = availablePlanData.selectedPolicyId;
                                history.push(`${match.url}/policy-subscription/${policyId}/beneficiaries`);
                            }
                            else {
                                onPageForward();
                            }
                        }
                    }
                });
            }
            else {
                if (ap.existingPolicyId !== null && ap.existingPolicyId > 0 && ap.terminateDate !== null) {
                    onPageForward();
                }
            }
        });

        // This must be set at the conclusion of this function, or we may see a perpetual loading spinner on navigation.
        setSaveAndContinueLoading(false);
    };

    /**
     * 
     * @param personId 
     * @param isParticipantBeingAdded 
     */
    const onUpdateParticipant = async (personId: PersonId, isParticipantBeingAdded: boolean) => {
        const currentParticipantsList = participantsList.current;
        const existingPersonIdIndex = currentParticipantsList.indexOf(personId);

        if (existingPersonIdIndex > -1) {
            // If the participant already exists in the array, but it's been flagged for deletion, delete it...
            if (!isParticipantBeingAdded) {
                currentParticipantsList.splice(existingPersonIdIndex, 1);
            }
        } else {
            // If the participant doesn't already exist in the array and it's been flagged as an add, add it in...
            if (isParticipantBeingAdded) {
                currentParticipantsList.push(personId);
            }
        }

        if(isMounted) {
            await fetchPlanRates();
        }
    };

    const onVolumeChange = (planIndex: number, volume: string, isDropdown = false) => {
        if (!isMounted) {
            return;
        }

        let volumeAsFloat = parseFloat(volume);

        // This accounts for empty strings and I'm not sure what other scenarios - set back to zero if we get an empty string.
        // Note: As far as I know, this ONLY covers what happens when text is cleared in an FSA-type plan -- we take their attempt to clear, and set it back to zero.

        let validInFlightVolume: Nullable<string> = null;

        if (Number.isNaN(volumeAsFloat) || !Number.isFinite(volumeAsFloat) || volumeAsFloat < 0) {
            volumeAsFloat = 0;
        } else {
            // We're working with a valid number.
            const MaxNumberOfDecimalDigitsAllowed = 2;

            const firstDecimalPointIndex = volume.indexOf('.');
            const digitsAfterDecimalPoint = firstDecimalPointIndex === -1 ? '' : volume.substring(volume.indexOf('.') + 1);
            if (digitsAfterDecimalPoint.length > MaxNumberOfDecimalDigitsAllowed) {
                const numberOfDigitsToTrim = digitsAfterDecimalPoint.length - MaxNumberOfDecimalDigitsAllowed;
                validInFlightVolume = volume.substring(0, volume.length - numberOfDigitsToTrim);

                // Because we are truncating the volume as a string above, the "real" volume also needs to be updated or
                // it could be one digit more precise than the actual string value.
                volumeAsFloat = parseFloat(validInFlightVolume);
            } else {
                validInFlightVolume = volume;
            }

            // Removing leading 0's. This way the user can overwrite a 0 just by typing.
            validInFlightVolume = validInFlightVolume.replace(/^0+/, '');
        }

        // This is used in a limited context to keep track of half volumes, such as entered decimal points.
        inFlightStringVolumeMapper[planIndex] = validInFlightVolume;
        setInFlightStringVolumeMapper([...inFlightStringVolumeMapper]);


        volumeMapper[planIndex] = volumeAsFloat;

        // Create a new array to guarantee state gets updated - volume mapper is used in render.
        setVolumeMapper([...volumeMapper]);

        // Note: While the below calls depend on volumeMapper being up to date, because its reference
        // is all that is altered above, it will continue to be up to date even without a callback.
        if(isDropdown) {
            fetchPlanRates(planIndex, volumeAsFloat);
        } else {
            debounceFetchPlanRates(planIndex, volumeAsFloat, true);
        }
    };

    /** Note! I explicitly typed out the return of this function ONLY because the "debounce" method it's calling isn't typed, and I am a little afraid to attempt to type it. */
    const debounceFetchPlanRates: (planIndex: number, volume: Decimal, focusInput: boolean) => void = debounce((planIndex: number, volume: Decimal, focusInput: boolean) => {
        fetchPlanRates(planIndex, volume, focusInput);
    }, 150);

    const fetchPlanRates = async (planIndex?: number, volume?: Decimal, focusInput?: boolean) => {
        if(!activePackage || !isMounted || !availablePlanData) {
            // do not proceed if activePackage is null - throws errors referencing activePackage.* properties
            return;
        }

        const plans: PlanRateRequest[] = [];

        availablePlanData.availablePlans.forEach((ap, index) => {
            const classPlanId = ap.planAvailableToClassId;
            const startDate = ap.rates.effectiveDate;
            plans.push({
                enrollmentPackageId: activePackage.enrollmentPackageId,
                planAvailableToClassId: classPlanId,
                startDate: startDate,
                participantPersonIds: participantsList.current,
                selectedVolume: volumeMapper[index] ?? (ap.existingPolicyVolumeInUse ? ap.existingPolicyVolumeInUse! : ap.minimumAvailableVolume ? ap.minimumAvailableVolume! : 0),
                useDefinedContribution: useDefinedContribution.current
            });
        });

        if (planIndex !== null && planIndex !== undefined && volume !== null && volume !== undefined) {
            plans[planIndex].selectedVolume = volume;
        }

        setPlanRatesLoading(true);
    
        const results = await getPlanRates(activePackage.enrollmentPackageId, plans);

        if (!isMounted) { return; }

        setPlanRatesLoading(false);

        // ToDo: Swap with UseApiCall hook - removes need for mount check, loading state, and result state.
        setRatesList(results);
        
        if(focusInput && volumeInput.current) {
            volumeInput.current.focus();
        }
    };

    
    const isPlanVolumeInvalid = (plan: AvailablePlanDetail, planIndex: number) => {
        if (plan.rateTypeId === rateTypeFSASelectMatch() && planTypeShortName !== 'HRA') {
            const curVol = volumeMapper[planIndex];

            if (
                curVol !== null && (curVol < plan.minimumAvailableVolume! || curVol > plan.maximumAvailableVolume!)) {
                return true;
            }
        }

        return false;
    };

    // Render Helper Logic ====================================================================

    const renderEnrollmentContent = (enrollmentContentHtml: string) => {
        return <div dangerouslySetInnerHTML={{ __html: enrollmentContentHtml }} />;
    };

    const renderDependents = (policyId: PolicyId, dependents: readonly PersonRelative[], lockedOn: boolean, frozen: boolean) => {
        return dependents.map(dependent => {
            const isYesSelected = isSelected(policyId, dependent.relativePersonId);

            // IMPORTANT! - I removed this line in an earlier commit thinking that it was useless.
            // The name is what's tying pairs of radios together. Without this, toggling one radio in a pair won't untoggle the other!!
            const fieldName = "Participant_" + dependent.relativePersonId;

            return (
                <ListGroup.Item className="d-flex justify-content-between" key={dependent.relativePersonId}>
                    {`Name: ${dependent.name}`}
                    <div>
                        <Form.Check name={fieldName} type="radio" onChange={() => onUpdateParticipant(dependent.relativePersonId, true)} inline value="Y" label="Yes" defaultChecked={isYesSelected} disabled={frozen || dependent.isTerming}/>
                        <Form.Check name={fieldName} type="radio" onChange={() => onUpdateParticipant(dependent.relativePersonId, false)} inline value="N" label="No" defaultChecked={!isYesSelected} disabled={frozen || (lockedOn && isYesSelected)}/>
                    </div>
                </ListGroup.Item>
            );
        });
    };

    const renderPlans = (plans: readonly AvailablePlanDetail[], planTypeShortName: string, addCoverageDisabled: boolean) => {
        return plans.map((plan, planIndex) => {

            // Note: I don't think rates are actually nullable, but the original code had a null check, so I'll leave it just in case...
            const startDate = plan.rates?.effectiveDate ?? '';

            const volumes = [];
            if (plan.rateTypeId === rateTypeVolume() && plan.volumeSource === VolumeSource.SELECT && plan.volumeListStep !== 0) {

                if (plan.volumeIsLockedToMaximum === true) {
                    volumes.push(<option value={plan.maximumAvailableVolume!}>{generateVolumeText(plan.maximumAvailableVolume, plan.shouldDisplayVolumeAsPlainNumber)}</option>);
                }
                else {
                    if (plan.volumeSimpleListArray === null) {
                        for (let i = plan.minimumAvailableVolume!; i < (plan.maximumAvailableVolume! + 1); i += plan.volumeListStep!) {
                            volumes.push(<option value={i} key={i}>{generateVolumeText(i, plan.shouldDisplayVolumeAsPlainNumber)}</option>);
                        }
                    }
                    else {
                        for (let i = 0; i < plan.volumeSimpleListArray.length; i++) {
                            volumes.push(<option value={plan.volumeSimpleListArray[i]} key={i}>{generateVolumeText(plan.volumeSimpleListArray[i], plan.shouldDisplayVolumeAsPlainNumber)}</option>);
                        }
                    }
                }
            }

            // Not sure what scenario this fallback is for, but have to handle it regardless.
            const rates = ratesList[planIndex] ?? plan.rates;
            const currentVolumeMapperValue = volumeMapper[planIndex];
            const currentInFlightVolumeMapperValue = inFlightStringVolumeMapper[planIndex];

            return (
                <tr key={plan.planName}>
                    <td className={classnames("vam", plan.hasExistingPolicy ? 'in-cart' : 'no-policy-in-cart')}>
                        {plan.hasExistingPolicy && <FaCheckCircle className="good icon-large hide-cell" />}
                    </td>
                    
                    <td className="vam"><CarrierImage altText={plan.carrierName} carrierId={plan.carrierId} carrierLogoFileName={plan.carrierLogoFileName} /></td>
                    <td className="vam">{plan.planName}</td>

                    { /* Note about the "text-nowrap" class below - simply to keep dollar sign & dropdown ALWAYS adjacent. If you don't find that class below in the future, please remove this comment.*/}
                    {shouldShowVolumeColumn(plans) &&
                        <td className="vam volume-cell">
                            {plan.rateTypeId === rateTypeVolume() && plan.volumeSource === VolumeSource.SELECT ?
                                <span className="bold text-nowrap">{!plan.shouldDisplayVolumeAsPlainNumber && '$'}
                                    <select 
                                        value={currentVolumeMapperValue?.toString()} 
                                        onChange={e => {
                                            if (!plan.toBeRemoved) {
                                                onVolumeChange(planIndex, e.target.value, true);
                                            }
                                        }}
                                    >
                                        {volumes}
                                    </select>
                                </span>
                                : <span className="bold">{!plan.shouldDisplayVolumeAsPlainNumber && '$'}{generateVolumeText(plan.existingPolicyVolumeInUse, plan.shouldDisplayVolumeAsPlainNumber)}</span>
                            }
                        </td>
                    }
                    <td className="text-nowrap text-medium tc vam contribution-cell">
                        {plan.toBeRemoved ?
                            <span className="error"><b>Will Terminate on {moment(plan.terminateDate).format("YYYY-MM-DD")}</b></span>
                            :
                            plan.rateTypeId === rateTypeFSASelectMatch()
                            ? <ListGroup>
                                    { planTypeShortName !== 'HRA' &&
                                    <ListGroup.Item className="d-flex justify-content-between">
                                        <span>You Pay <span className="error"> Per Paycheck:</span></span>
                                        <span className="bold">$
                                            <input type="string" className="text-right"
                                                onChange={e => onVolumeChange(planIndex, e.target.value)}
                                                value={currentInFlightVolumeMapperValue ?? currentVolumeMapperValue?.toString()}
                                                ref={volumeInput} />
                                        </span>
                                    </ListGroup.Item>
                                    }
                                    { !shouldHideEmployerContributions &&   
                                    <ListGroup.Item className="d-flex justify-content-between">
                                        <span>Employer Pays <span className="error">Per Paycheck:</span></span>
                                        {!planRatesLoading && <span>${formatCurrency(rates.employerRate)}</span>}
                                    </ListGroup.Item>
                                    }                           

                                    {planRatesLoading ? null :
                                        plan.planYearToDateContribution > 0 ?
                                    <React.Fragment>
                                        <ListGroup.Item className="d-flex justify-content-between">
                                            <span className="bold">Already Contributed this Year:</span>
                                            <span className="bold">${formatCurrency(plan.planYearToDateContribution)}</span>
                                        </ListGroup.Item>  
                                        <ListGroup.Item className="d-flex justify-content-between">
                                            <span className="bold">Remaining Contribution:</span>
                                            <span className="bold">${formatCurrency(rates.annualContribution)}</span>
                                        </ListGroup.Item>
                                    </React.Fragment>
                                    : null
                                    }
                                    <ListGroup.Item className="d-flex justify-content-between">
                                        <span className="bold">Annual Contribution:</span>
                                        <span className="bold">${formatCurrency(rates.annualContribution! + plan.planYearToDateContribution)}</span>
                                    </ListGroup.Item>
                            </ListGroup>
                            :
                            <ListGroup variant="flush">
                                {!shouldHideEmployeeContributions && <ListGroup.Item className="d-flex justify-content-between">
                                    <span>You Pay <span className="error"> Per Paycheck:</span></span>
                                    <span className="bold">${formatCurrency(rates.employeePreTaxRate + rates.employeePostTaxRate)}</span>
                                </ListGroup.Item>
                                }
                                {!shouldHideEmployerContributions && <ListGroup.Item className="d-flex justify-content-between">
                                    <span>Employer Pays <span className="error">Per Paycheck:</span></span>
                                    {planRatesLoading ? null :
                                        <span>${formatCurrency(rates.employerRate)}</span>
                                    }
                                </ListGroup.Item>
                                }
                            </ListGroup>
                        }
                    </td>
                    <td className="tc vam action-cell">
                        <ButtonToolbar
                            className="justify-content-around"
                            aria-label="Available Plan Actions"
                        >
                            {plan.hasExistingPolicy && plan.isBeneficiariesRequired && <button type="button" onClick={() => viewBeneficiaries()} className="btn btn-sm btn-outline-success mr-2"><FaEdit /> Beneficiaries</button>}

                            {plan.canAddCoverage &&
                                <button type="button" className="btn btn-addswitch btn-sm" onClick={() => onAddPolicy(plan.planAvailableToClassId, startDate, planIndex, plan)} disabled={addCoverageDisabled}><FaPlus /> Add Coverage</button>
                            }
                            {plan.canChangeToCoverage &&
                                <button type="button" className="btn btn-addswitch btn-sm" onClick={() => onChangeTo(plan.planAvailableToClassId, startDate, planIndex, plan)}><FaRetweet /> Change To</button>
                            }
                            {plan.canRemoveCoverage &&
                                <DeleteConfirm message="Are you sure you want to remove or terminate this policy?" confirmButtonText="Yes - Remove Policy" closeButtonText="No - Close" headerText="Remove / Terminate Policy" onDeleteClick={() => onRemovePolicy(plan.existingPolicyId!)} tooltipMessage="Remove Plan" />
                            }

                            {plan.canChangeBackToCoverage &&
                                <button type="button" className="btn btn-addswitch btn-sm" onClick={() => onChangeBackTo(plan.existingPolicyId!)}><FaRetweet /> Change Back To</button>
                            }

                            {plan.canUndoRemoveCoverage &&
                                <button type="button" className="btn btn-primary btn-sm" onClick={() => onUndoRemovePolicy(plan.existingPolicyId!)}><FaPlus /> Undo Term</button>
                            }

                            {plan.hasExistingPolicy && !plan.canRemoveCoverage && plan.carrierContactForRemoval
                                ? <div>{plan.carrierContactForRemoval}</div> : null}
                        </ButtonToolbar>
                    </td>
                </tr>
            );
        });
    };

    // Main Rendering Logic Here ======================================================================

    // Note: This is new. I don't see why we would granularly load in any of the below if these two data points are not yet available.
    // Just back out - this also removes the need for null checks below.
    const planSelectionLoading = availablePlansLoading || saveAndContinueLoading;

    if (!activePackage || !availablePlanData || planSelectionLoading) {
        return <ContentCard loading />;
    }

    // ToDo: Account for a possible error state. The above assumes that no data === we're still waiting for it.
    // This is still the same as before, but it could obviously be better.

    // Plan Type Label
    const planType = getPlanType(planTypeId, activePackage.planTypes);
    const planTypeName = planType?.planTypeName ?? '';
    const planTypeShortName = planType?.planTypeShortName ?? '';

    // Contribution warning
    let contributionWarning: Nullable<string> = null;
    let areAnyVolumesInvalid = false;

    availablePlanData.availablePlans.forEach((ap, planIndex) => {
        const planVolumeIsInvalid = isPlanVolumeInvalid(ap, planIndex);
        if (planVolumeIsInvalid) {
            contributionWarning = " Your Contribution Per Paycheck must be between $" + formatCurrency(ap.minimumAvailableVolume) + " and $" + formatCurrency(ap.maximumAvailableVolume);
            areAnyVolumesInvalid = true;
        }
    });
    
    // Enrollment Content
    const enrollmentContentHtml = getEnrollmentContentFromCurrentStep();

    return (
        <React.Fragment>
            <ContentCard>
                <h2>{planTypeName}</h2>
                {renderEnrollmentContent(enrollmentContentHtml)}
            </ContentCard>

            {/* Plan Documents */}
            <PlanDocuments groupId={activePackage.groupId} availablePlans={availablePlanData.availablePlans} />

            {/* Dependents */}
            {availablePlanData.relatives.length > 0 && shouldShowDependents(availablePlanData.availablePlans) &&
                <Card>
                    <Card.Body>
                        <h2>Dependents</h2>
                        <Card.Title>Do you want to provide coverage for:</Card.Title>
                        <ListGroup>
                            {renderDependents(availablePlanData.selectedPolicyId, availablePlanData.relatives, availablePlanData.dependentsAreLockedOn, availablePlanData.dependentsAreFrozen)}
                        </ListGroup>
                    </Card.Body>
                </Card>
            }

            <Card className="plan-selector-card"> 
                <Card.Body>
                    {/* Header Area */}
                    <div className="d-flex justify-content-between align-items-center">
                        <h2>Available Policies</h2>
                        { !shouldHideAllContributionInformation && <span className="error sidenote mt-1 bold">Contributions are per Paycheck</span> }
                    </div>
                    <Table size="sm" hover responsive className="plan-selector-view-table">
                        <thead>
                            <tr>
                                <th />
                                <th>Carrier</th>
                                <th>Policy</th>
                                {shouldShowVolumeColumn(availablePlanData.availablePlans) && <th>Volume</th>}
                                <th>{!shouldHideAllContributionInformation ? 'Contributions' : ''}</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {renderPlans(availablePlanData.availablePlans, planTypeShortName, areAnyVolumesInvalid)}
                        </tbody>
                    </Table>
                    <ButtonToolbar
                        className="justify-content-between"
                        aria-label="Enrollment Navigation"
                    >
                        <Button disabled={!activePackage} size="sm" variant="default" type="button" onClick={() => onPageBack()} ><FaAngleDoubleLeft /> Back</Button>
                        {contributionWarning &&
                            <span className="bold"><FaExclamationCircle className="error" />{contributionWarning}</span>
                        }
                        <ButtonGroup>      
                            {availablePlanData.canDecline &&
                                <Button disabled={!activePackage} size="sm" className={classnames('btn-declineremove', (availablePlanData.canContinue || availablePlanData.canSaveChanges) && 'mr-2')} onClick={() => onDecline()}><FaBan /> Decline &amp; Continue <FaAngleDoubleRight /></Button>
                            }
                            {(availablePlanData.canContinue || availablePlanData.canSaveChanges) &&
                                <Button disabled={areAnyVolumesInvalid} className="btn btn-primary btn-sm float-right" onClick={() => onSaveAndContinue(availablePlanData.selectedPolicyId)}>Save &amp; Continue <FaAngleDoubleRight /></Button>
                            }
                        </ButtonGroup>
                    </ButtonToolbar>
                </Card.Body>
            </Card>
        </React.Fragment>
    );
};

// Helpers

const getNextStep = (activePackage: Package, planTypeId: PlanTypeId, direction: 1 | -1) => {
    if (activePackage.steps) {
        for (let i = 0; i < activePackage.steps.length; i++) {
            if (activePackage.steps[i].type === PackageStepType.PlanType && activePackage.steps[i].relatedEntityId === planTypeId) {
                return activePackage.steps[i + direction];
            }
        }
    }
    return null;
};

const getNextStepRoute = (activePackage: Package, planTypeId: PlanTypeId) => {
    const nextStep = getNextStep(activePackage, planTypeId, 1);
    if (nextStep) {
        if (nextStep.type === PackageStepType.PlanType && nextStep.relatedEntityId) {
            if(nextStep.harmonyProductGroup) {
                return `/packages/${activePackage.enrollmentPackageId}/harmony/${nextStep.harmonyProductGroup}`;
            } else {
                return `/packages/${activePackage.enrollmentPackageId}/plantypes/${nextStep.relatedEntityId}`;
            }
        } else {
            if (nextStep.type === PackageStepType.Insurability) {
                return `/packages/${activePackage.enrollmentPackageId}/insurability`;
            } else {
                return `/packages/${activePackage.enrollmentPackageId}/summary`;
            }
        }
    } else {
        // if cannot determine next step, return current step
        return `/packages/${activePackage.enrollmentPackageId}/plantypes/${planTypeId}`;
    }
};

const getPreviousStepRoute = (activePackage: Package, planTypeId: PlanTypeId) => {
    const previousStep = getNextStep(activePackage, planTypeId, -1);
    if (previousStep) {
        if (previousStep.type === PackageStepType.PlanType && previousStep.relatedEntityId) {
            if(previousStep.harmonyProductGroup) {
                return `/packages/${activePackage.enrollmentPackageId}/harmony/${previousStep.harmonyProductGroup}`;
            } else {
                return `/packages/${activePackage.enrollmentPackageId}/plantypes/${previousStep.relatedEntityId}`;
            }
        } else if (previousStep.type === PackageStepType.DocumentUpload) {
            return `/packages/${activePackage.enrollmentPackageId}/documents`;
        } else {
            return `/packages/${activePackage.enrollmentPackageId}/dependents`;
        }
    } else {
        // if cannot determine next step, return current step
        return `/packages/${activePackage.enrollmentPackageId}/plantypes/${planTypeId}`;
    }
};

const getPlanType = (planTypeId: PlanTypeId, planTypes: readonly AvailablePlanType[]) => {
    if (planTypeId && planTypes && planTypes.length > 0) {
        return planTypes.find(pt => pt.planTypeId === planTypeId);
    }
};

const shouldShowDependents = (plans: readonly AvailablePlanDetail[]) => {
    const doAnyPlansHaveDependentRateTypes = plans.some(p => p.rateTypeId === rateTypeDependent());
    return doAnyPlansHaveDependentRateTypes;
};

const shouldShowVolumeColumn = (plans: readonly AvailablePlanDetail[]) => {
    const doAnyPlansHaveVolumeRateTypes = plans.some(p => p.rateTypeId === rateTypeVolume());
    return doAnyPlansHaveVolumeRateTypes;
};

const generateVolumeText = (volume: number | null, shouldDisplayVolumeAsPlainNumber: boolean): string => {
    if (volume === null) {
        return '';
    }

    // Special case - right now this is only used for PET plans - we don't want a dollar sign or floating point.
    if (shouldDisplayVolumeAsPlainNumber) {
        return volume.toString();
    }

    return formatCurrency(volume);
};

export default PlanSelector;